import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InventoryManager from './InventoryManager';

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';

// Usernames with Operations access (mirrors menuModel gate 'operations' + backend
// INVENTORY_VIEW_ALL). This component is a defensive no-op for anyone else.
const OPERATIONS_USERS = ['memmetti', 'jlamirault', 'sfoley', 'ptaylor'];

// The Operations hub — one top-level page with an internal tab bar, mirroring the
// Human Resources and Facilities hubs. Overview is a roll-up; Inventory is the full
// asset register console (InventoryManager) with granular filtering + import/export.
export default function Operations({ currentUser }) {
  const [tab, setTab] = useState('overview');

  const allowed = OPERATIONS_USERS.includes(currentUser?.username);
  if (!allowed) {
    return (
      <div style={{ padding: '48px 28px', textAlign: 'center' }}>
        <h2 style={{ color: NAVY }}>Operations</h2>
        <p style={{ color: '#9CA3AF' }}>This department is restricted.</p>
      </div>
    );
  }

  const tabs = [
    { key: 'overview',  label: 'Overview' },
    { key: 'inventory', label: 'Inventory' },
  ];

  const renderTab = () => {
    switch (tab) {
      case 'overview':  return <Overview onNavigate={setTab} />;
      case 'inventory': return <InventoryManager />;
      default:          return null;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '2px', padding: '0 28px', borderBottom: '1px solid #EDEDF0', background: '#fff', overflowX: 'auto' }}>
        {tabs.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding: '15px 16px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: '14px', fontWeight: active ? 700 : 500, color: active ? NAVY : '#8A8A8E',
                borderBottom: active ? `2.5px solid ${ORANGE}` : '2.5px solid transparent',
                whiteSpace: 'nowrap', marginBottom: '-1px', transition: 'color .15s',
              }}>
              {t.label}
            </button>
          );
        })}
      </div>
      {/* Inventory renders its own full-bleed console; Overview gets hub padding. */}
      <div style={{ padding: tab === 'inventory' ? 0 : '24px 28px' }}>{renderTab()}</div>
    </div>
  );
}

// ---- shared building blocks (match HR / Facilities palette) ----
const card = { background: '#fff', border: '1px solid #EDEDF0', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const label = { fontSize: '11.5px', fontWeight: 600, color: '#8A8A8E', textTransform: 'uppercase', letterSpacing: '0.05em' };

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: '18px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h2>
      {subtitle && <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0' }}>{subtitle}</p>}
    </div>
  );
}

// ---- Overview ----
function Overview({ onNavigate }) {
  const [stats, setStats] = useState(null); // { total, assigned, unassigned }
  useEffect(() => {
    axios.get(API + '/inventory', { headers: headers() })
      .then(r => {
        const items = r.data || [];
        const assigned = items.filter(i => i.staff_id).length;
        setStats({ total: items.length, assigned, unassigned: items.length - assigned });
      })
      .catch(() => setStats({ total: 0, assigned: 0, unassigned: 0 }));
  }, []);

  const cards = [
    { key: 'inventory', title: 'Total Assets', big: stats ? String(stats.total) : '—', sub: 'in the register' },
    { key: 'inventory', title: 'Assigned',      big: stats ? String(stats.assigned) : '—', sub: 'to staff' },
    { key: 'inventory', title: 'Unassigned',    big: stats ? String(stats.unassigned) : '—', sub: 'available / in stock' },
  ];
  const hover = e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.09)'; };
  const out = e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; };
  return (
    <div>
      <SectionHeader title="Operations" subtitle="Manage the museum's complete asset register — from hard drives to furniture." />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
        {cards.map((c, i) => (
          <div key={i} style={{ ...card, padding: '20px 22px', cursor: 'pointer', transition: 'transform .15s, box-shadow .15s' }}
            onClick={() => onNavigate(c.key)} onMouseEnter={hover} onMouseLeave={out}>
            <div style={{ ...label, marginBottom: '10px' }}>{c.title}</div>
            <div style={{ fontSize: '34px', fontWeight: 700, lineHeight: 1, color: NAVY }}>{c.big}</div>
            <div style={{ fontSize: '12.5px', color: '#6B7280', marginTop: '9px' }}>{c.sub}</div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '16px' }}>Open the <strong>Inventory</strong> tab for the full asset console — granular filtering, import/export, and printable reports.</p>
    </div>
  );
}
